/* eslint-disable @next/next/no-img-element */
import { Know, KnowFormat } from 'interfaces/Know'
import styled from 'styled-components'
import LoadingArea from 'components/elements/LoadingArea'
import theme from 'stories/utils/theme'
import { Body2, ButtonText, Headline4, Subtitle1 } from 'stories/elements/Typography/Text'
import PDFIcon from 'assets/icons/pdf.svg'
import { parseLocale } from 'lib/constants/localization'
import { getFirstLocalSubjectFromKnowSubjects } from 'lib/formatting/subjects'
import { useRouter } from 'next/router'
import Stack from 'components/elements/Stack'
import KnowLikes from 'assets/icons/webapp_liked.svg'
import { ClientOnly } from 'components/elements/ClientOnly'
import { colorToCssFilter } from 'lib/features/color'
import KnowImpressionTracking from 'lib/tracking/KnowImpressionTracking'
import FlashcardInfoPreview from 'stories/elements/Know/FlashcardInfo'

const Container = styled.div`
    width: 164px;
    height: 210px;
    padding-bottom: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.card};
    border: 2px solid ${theme.colors.violet};
    cursor: pointer;
    position: relative;

    .icon {
        padding: 2.5px 5px;
        display: flex;
        gap: 7.5px;
        svg {
            position: relative;
            top: 3px;
            overflow: visible;
        }
    }

    .stats {
        padding: 0 !important;

        display: flex;
    }
    a {
        text-decoration: none !important;
    }

    @media (max-width: 750px) {
        width: 180px;
    }
`

const EmptyPreview = styled.div`
    height: 110px;
    width: 164px;
    background: ${theme.colors.deepSeaBlue};

    display: grid;
    place-content: center;

    svg {
        fill: white;
        width: 50px;
        height: 50px;
    }
`

const ThumbnailImage = styled.img`
    object-fit: none !important;
    object-position: top;
`

const Description = styled(Body2)`
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 400;
`

const HeadlineSeo = styled(Headline4)`
    font-weight: 600;
    max-width: 140px;
    overflow: hidden;
    font-size: 14px;
    line-height: 18px;
    text-overflow: ellipsis;
    color: ${theme.colors.actionGray};
`

const HeadlineNormal = styled(Subtitle1)`
    font-weight: 600;
    max-width: 140px;
    overflow: hidden;
    font-size: 14px;
    text-overflow: ellipsis;
    color: ${theme.colors.actionGray};
`

const InformationWrapper = styled(Stack)`
    padding: 5px 12px 10px 12px;
`

const SubjectWrapper = styled.div<{ iconColor?: string }>`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${theme.colors.white};
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
    display: grid;
    place-content: center;
    position: absolute;
    right: 10px;
    top: 90px;
    & img {
        filter: ${(props) => props.iconColor};
    }
`

const loadingAreas = {
    know: 'knowContent',
}

interface Props {
    know: Know
    showTitleAsSubHeadline?: boolean
}

const KnowDisplayBody = ({ know, showTitleAsSubHeadline }: Props) => {
    const router = useRouter()
    const { locale } = router
    const parsedLocale = parseLocale(locale)
    const localizedSubject = know ? getFirstLocalSubjectFromKnowSubjects(know?.subjects, parsedLocale.countryCode) : null

    const iconColorFilter = localizedSubject ? colorToCssFilter(localizedSubject?.color) : undefined

    const showFlashcardsIcon = know.knowFormat === KnowFormat.Flashcards

    const Headline = showTitleAsSubHeadline ? HeadlineSeo : HeadlineNormal

    return (
        <>
            {know ? <KnowImpressionTracking know={know} /> : null}
            <Container>
                <ClientOnly>
                    {localizedSubject ? (
                        <SubjectWrapper iconColor={iconColorFilter}>
                            <img src={localizedSubject.iconUrl} alt={localizedSubject.name} width={25} height={25} />
                        </SubjectWrapper>
                    ) : null}
                </ClientOnly>
                <LoadingArea area={loadingAreas.know}>
                    <div className="image">
                        {know?.thumbnailSmallUrl ? (
                            <ThumbnailImage
                                src={know.thumbnailSmallUrl}
                                alt={`Know ${know.seoTitle ?? know.title} thumbnail`}
                                width={164}
                                height={110}
                                loading="lazy"
                            />
                        ) : (
                            <EmptyPreview>
                                {showFlashcardsIcon ? <FlashcardInfoPreview know={know} isSmall /> : <PDFIcon />}
                            </EmptyPreview>
                        )}
                    </div>
                    <InformationWrapper gutter={5}>
                        <div className="stats">
                            <div className="icon">
                                <KnowLikes />
                                <ButtonText color={theme.colors.actionGray}>{know.likes}</ButtonText>
                            </div>
                        </div>
                        <Stack gutter={0}>
                            <Headline fontSize={15} color={theme.colors.headlineDark} fontFamily="var(--font-inter)">
                                {know.seoTitle ?? know.title}
                            </Headline>
                            <Description fontFamily="var(--font-inter)">
                                {know.seoDescription ?? know.description}
                            </Description>
                        </Stack>
                    </InformationWrapper>
                </LoadingArea>
            </Container>
        </>
    )
}

export default KnowDisplayBody
